/**
 * @description Hook utilizado para manipular os casos
 * @author Diego Sabino Amorim de Araújo
 * @file useCasoService.js
 * @license Copyright (c) 2022 SIGEP DIGETI ITEP/RN
 */

import actionTypes from 'utils/actionTypes'
import { useDispatch } from 'react-redux'
import { setProcessando } from 'actions'
import { useFetch } from './useFetch'

export const useCasoService = () => {
  const fetch = useFetch()
  const dispatch = useDispatch()

  const listarCasos = async () => {
    try {
      dispatch(setProcessando(true, 'CASO'))
      const response = await fetch.get(`casos/comSetores`)
      dispatch({
        type: actionTypes.FETCH_CASOS,
        casos: response.data.casos,
      })
    } catch (err) {
      return
    } finally {
      dispatch(setProcessando(false, 'CASO'))
    }
  }

  const listarCaso = async (id, setCaso, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`casos/${id}`)
      setCaso(response.data.caso)
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCasoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`casos/${id}`)
      return response.data.caso
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }
  
  const listarCasoComContagem = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`casos/${id}/comcontagem`)
      return response.data.dados
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarArquivos = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`casos/${id}/arquivos`)
      return response.data.arquivos
    } catch (err) {
      return
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCasoDetalhadoAsync = async (id, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`casos/detalharCaso/${id}`)
      return response.data.caso
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  const listarCasoGlobal = async (id) => {
    try {
      dispatch(setProcessando(true, 'CASO'))
      const response = await fetch.get(`casos/detalharCaso/${id}`)

      dispatch({
        type: actionTypes.FETCH_CASO,
        caso: response.data.caso,
      })
    } catch (err) {
      return
    } finally {
      dispatch(setProcessando(false, 'CASO'))
    }
  }

  const listarMeusCasos = async () => {
    try {
      dispatch(setProcessando(true, 'CASO'))
      const response = await fetch.get(`casos/doUsuario`)
      dispatch({
        type: actionTypes.FETCH_MEUS_CASOS,
        meusCasos: response.data.casos,
      })
    } catch (err) {
      return
    } finally {
      dispatch(setProcessando(false, 'CASO'))
    }
  }

  const listarMeusCasosAsync = async () => {
    try {
      const response = await fetch.get(`casos/doUsuario`)
      return response.data?.casos || []
    } catch (err) {
      console.error('Erro ao listar meus casos:', err)
      return []
    }
  }

  const listarCasosDoSetor = async () => {
    try {
      dispatch(setProcessando(true, 'CASO'))
      const response = await fetch.get(`casos/dosSetoresUsuario`)
      dispatch({
        type: actionTypes.FETCH_CASOS_DO_SETOR,
        casosDoSetor: response.data.casos,
      })
    } catch (err) {
      return
    } finally {
      dispatch(setProcessando(false, 'CASO'))
    }
  }

  const listarCasosDoSetorAsync = async () => {
    try {
      const response = await fetch.get('casos/dosSetoresUsuario')
      return response.data?.casos || []
    } catch (err) {
      console.error('Erro ao listar casos do setor:', err)
      return []
    }
  }

  const deletarCaso = async (id, setProcessando) => {
    if (setProcessando) setProcessando(true)
    await fetch.del(`/casos/${id}`)
    if (setProcessando) setProcessando(false)
    dispatch({
      type: actionTypes.FETCH_CASO,
      caso: null,
    })
  }

  const cadastrarCaso = async (dados, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const novoCaso = {
      descricao: dados.descricao,
      setorResponsavel: dados.setorResponsavel?.id || null,
      setoresInteressados: dados.setoresInteressados?.map((setor) => setor.id || null),
      processoSei: dados.processoSei,
      data: dados.data,
      arqs: dados.arqs,
    }
    const response = await fetch.postOpen(`/casos/`, novoCaso)
    if (setProcessando) setProcessando(false)
    return response.data.caso
  }

  const editarCaso = async (id, dados, setProcessando) => {
    if (setProcessando) setProcessando(true)
    const caso = {
      descricao: dados.descricao,
      setorResponsavel: dados.setorResponsavel?.id || null,
      setoresInteressados: dados.setoresInteressados?.map((setor) => setor.id || null),
      processoSei: dados.processoSei,
      data: dados.data,
      arqs: dados.arqs,
    }
    const response = await fetch.putOpen(`/casos/${id}`, caso)
    if (setProcessando) setProcessando(false)
    return response.data.caso
  }

  /* Verificar se já existe caso criado com o numero RIP informado */
  const listarCasoPorIdentificadorRIP = async (identificadorCasoRip, setProcessando) => {
    try {
      if (setProcessando) setProcessando(true)
      const response = await fetch.get(`/casos/listarCasoPorIdentificadorRIP/${identificadorCasoRip}`)
      return response.data.caso
    } catch (err) {
      return null
    } finally {
      if (setProcessando) setProcessando(false)
    }
  }

  return {
    listarCasos,
    listarCaso,
    listarCasoAsync,
    listarCasoComContagem,
    listarCasoGlobal,
    listarMeusCasos,
    listarMeusCasosAsync,
    listarCasosDoSetor,
    listarCasosDoSetorAsync,
    deletarCaso,
    cadastrarCaso,
    editarCaso,
    listarCasoDetalhadoAsync,
    listarCasoPorIdentificadorRIP,
    listarArquivos,
  }
}
